// skipcq: JS-W1028
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { auth, logout, sendVerificationEmailToUser } from '../utils/firebase';
import { AuthSessionSyncContext, UserContext } from '../context/UserContext';
import mapFirebaseAuthError from '../utils/mapFirebaseAuthError';
import { getVerifiedUserDestination } from '../utils/verifiedUserDestination';

function reloadRequestedSilently(argument) {
  if (!argument || typeof argument !== 'object') {
    return false;
  }
  if (typeof Event !== 'undefined' && argument instanceof Event) {
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(argument, 'nativeEvent')) {
    return false;
  }
  return argument.silent === true;
}

function VerifyEmailPage() {
  const userCtx = useContext(UserContext);
  const refreshSession = useContext(AuthSessionSyncContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [busyAction, setBusyAction] = useState(null);
  const [banner, setBanner] = useState(null);
  const busyRef = useRef(false);

  useEffect(() => {
    const verificationEmailError = location.state?.verificationEmailError;
    if (typeof verificationEmailError !== 'string' || verificationEmailError.length === 0) {
      return;
    }
    setBanner({
      message:
        'Your account was created, but we could not send the verification email automatically. '
        + `${verificationEmailError} Use “Resend verification email” below.`,
      severity: 'warning',
    });
  }, [location.state]);

  const firebaseUser = auth.currentUser;

  const handleResend = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setBanner(null);
    busyRef.current = true;
    setBusyAction('resend');
    try {
      await sendVerificationEmailToUser(currentUser);
      await refreshSession();
      setBanner({
        message:
          'Verification email sent. Check your inbox and spam folder.',
        severity: 'success',
      });
    } catch (error) {
      setBanner({
        message: mapFirebaseAuthError(error),
        severity: 'error',
      });
    } finally {
      busyRef.current = false;
      setBusyAction(null);
    }
  }, [refreshSession]);

  const handleReloadStatus = useCallback(async (maybeOptions) => {
    const silent = reloadRequestedSilently(maybeOptions);
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    if (!silent) {
      setBanner(null);
    }
    busyRef.current = true;
    setBusyAction('reload');
    try {
      await currentUser.reload();
      const state = await refreshSession();
      if (state.emailVerified === true) {
        navigate(getVerifiedUserDestination(state), { replace: true });
      } else if (!silent) {
        setBanner({
          message:
            'We still show your email as unverified. If you clicked the link, wait a minute and try again.',
          severity: 'info',
        });
      }
    } catch (error) {
      if (!silent) {
        setBanner({
          message: mapFirebaseAuthError(error),
          severity: 'error',
        });
      }
    } finally {
      busyRef.current = false;
      setBusyAction(null);
    }
  }, [navigate, refreshSession]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || busyRef.current) {
        return;
      }
      handleReloadStatus({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [handleReloadStatus]);

  if (userCtx.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!userCtx.isLoggedIn || !firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  if (userCtx.emailVerified === true) {
    return <Navigate to={getVerifiedUserDestination(userCtx)} replace />;
  }

  const resending = busyAction === 'resend';
  const reloading = busyAction === 'reload';
  const displayEmail = userCtx.userEmail || firebaseUser?.email;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Paper elevation={2} sx={{ maxWidth: 460, width: '100%', p: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom align="center">
          Confirm your email
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mt: 1, mb: 2 }}
          align="center"
        >
          We sent a verification link to{' '}
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {displayEmail || 'your account email'}
          </Box>
          . Open it to activate your brewhouse access.
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          {banner ? (
            <Alert severity={banner.severity ?? 'error'}>
              {banner.message}
            </Alert>
          ) : null}

          <Button
            variant="contained"
            fullWidth
            disabled={Boolean(busyAction)}
            onClick={handleResend}
          >
            {resending ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Resend verification email'
            )}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            disabled={Boolean(busyAction)}
            onClick={handleReloadStatus}
          >
            {reloading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "I've verified — refresh status"
            )}
          </Button>

          <Button color="secondary" fullWidth onClick={logout}>
            Sign out
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default VerifyEmailPage;
